import {FC, SyntheticEvent, useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Trans, useTranslation} from "react-i18next";
import {isEmpty} from "lodash";
import styled from "styled-components";
import {clearUserErrors, saveProfile} from "modules/actions";
import {createConnextraScriptTag, getLogo} from "modules/utils";
import {
	getIsLoadingSaveProfile,
	getIsOpenSSOAdditionalFieldsModal,
	getUser,
	getUserErrors,
} from "modules/selectors";
import {BASE_URL} from "modules/constants";
import {
	Checkbox,
	Exist,
	FormError,
	FormField,
	ModalTitle,
	PrimaryButton,
	TextLink,
} from "components";
import {BaseModal} from "./BaseModal";

const FormContainer = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
	max-width: 320px;
	margin: 0 auto;
`;

const Icon = styled.img`
	width: 161px;
	margin-bottom: 24px;
`;

export const SSOAdditionalFieldsModal: FC = () => {
	const isOpen = useSelector(getIsOpenSSOAdditionalFieldsModal);

	if (!isOpen) {
		return null;
	}

	return <SSOAdditionalFieldsForm />;
};

export const SSOAdditionalFieldsForm: FC = () => {
	const dispatch = useDispatch();
	const {t, i18n} = useTranslation();
	const user = useSelector(getUser);
	const errors = useSelector(getUserErrors);
	const isShowMarketingEmails = !user?.optInGZ;
	const [isValid, setIsValid] = useState(false);
	const isLoading = useSelector(getIsLoadingSaveProfile);
	const hasError = !isEmpty(errors);
	const isDisabled = isLoading || hasError || !isValid;
	const lang = i18n.language;
	const logo = getLogo(lang);

	useEffect(() => {
		dispatch(clearUserErrors());
	}, [dispatch]);

	const handleSubmit = useCallback(
		(e: SyntheticEvent<HTMLFormElement>) => {
			e.preventDefault();
			const {terms, isEmailsEnabled}: Record<string, HTMLInputElement> = e.currentTarget;

			if (hasError) {
				return;
			}

			// If the field is not present, marketing emails have been enabled previously.
			const marketingEmails = isEmailsEnabled ? isEmailsEnabled.checked : true;

			dispatch(
				saveProfile({
					marketingEmails,
					terms: terms.checked,
				})
			);
		},
		[dispatch, hasError]
	);

	const handleOnChange = useCallback(
		(e: SyntheticEvent<HTMLFormElement>) => {
			const {terms}: Record<string, HTMLInputElement> = e.currentTarget;

			const validateList = [terms.checked];

			setIsValid(validateList.every((el) => el));
			dispatch(clearUserErrors());
		},
		[dispatch, setIsValid]
	);

	const marketingChangeHandler = useCallback(() => {
		createConnextraScriptTag(
			"https://zz.connextra.com/dcs/tagController/tag/8ce716f59752/email_optin"
		);
	}, []);

	if (!user) {
		return null;
	}

	return (
		<BaseModal className="grey">
			<form noValidate onSubmit={handleSubmit} onChange={handleOnChange}>
				<FormContainer>
					<Icon src={logo} alt="CFL Preseasons futures" />
					<ModalTitle>{t("modal.sso.title")}</ModalTitle>

					<FormField>
						<Checkbox
							name="terms"
							text={
								<Trans
									i18nKey="modal.sso.terms"
									components={{
										link1: (
											<TextLink
												href={`${BASE_URL}/help/terms`}
												target="_blank"
											/>
										),
									}}
								/>
							}
						/>
					</FormField>

					<Exist when={isShowMarketingEmails}>
						<FormField>
							<Checkbox
								name="isEmailsEnabled"
								text={t("modal.sso.marketing_emails")}
								onChange={marketingChangeHandler}
							/>
						</FormField>
					</Exist>

					<Exist when={Boolean(errors.form)}>
						<FormField>
							<FormError>{errors.form}</FormError>
						</FormField>
					</Exist>

					<FormField>
						<PrimaryButton disabled={isDisabled} type="submit">
							{t("modal.sso.button")}
						</PrimaryButton>
					</FormField>
				</FormContainer>
			</form>
		</BaseModal>
	);
};
